import { useCallback, useEffect, useRef, useState } from "react";
import { PrimaryButton } from "components/Button/style";
import {
  FrameLabel,
  PlacaFrame,
  StabilityBarFill,
  StabilityBarWrapper,
  StabilityLabel,
  StyledVideo,
  VideoWrapper,
} from "./style";

const STABLE_CHECKS_NEEDED = 15; // 1.5s @ 100ms
const PIXEL_DIFF_THRESHOLD = 12; // mean absolute diff por canal (0-255)

interface ICameraCaptureProps {
  onCaptura: (imagemBase64: string) => void;
  onCapturaManual?: (imagemBase64: string) => void;
  onErro: (mensagem: string) => void;
  ativa: boolean;
}

export function CameraCapture({ onCaptura, onCapturaManual, onErro, ativa }: ICameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const compareRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const lastFrameRef = useRef<ImageData | null>(null);
  const stabilityCountRef = useRef(0);
  const capturedRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [stabilidade, setEstabilidade] = useState(0);

  const pararCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    lastFrameRef.current = null;
    stabilityCountRef.current = 0;
    capturedRef.current = false;
    setEstabilidade(0);
  }, []);

  const capturar = useCallback(() => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) { return; }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")!.drawImage(video, 0, 0);
    const imagemBase64 = canvas.toDataURL("image/jpeg", 0.9);
    pararCamera();
    onCaptura(imagemBase64);
  }, [onCaptura, pararCamera]);

  const iniciarVerificacaoEstabilidade = useCallback(() => {
    if (intervalRef.current) { clearInterval(intervalRef.current); }

    intervalRef.current = setInterval(() => {
      const video = videoRef.current;
      const canvas = compareRef.current;
      if (!video || !canvas || video.readyState < 2 || capturedRef.current) { return; }

      const vw = video.videoWidth;
      const vh = video.videoHeight;
      if (!vw || !vh) { return; }

      // Amostra a área da placa (centro: 70% largura x 42% altura)
      const sw = Math.floor(vw * 0.7);
      const sh = Math.floor(vh * 0.42);
      const sx = Math.floor((vw - sw) / 2);
      const sy = Math.floor((vh - sh) / 2);

      const W = 80;
      const H = 32;
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, W, H);
      const current = ctx.getImageData(0, 0, W, H);

      if (lastFrameRef.current) {
        const prev = lastFrameRef.current.data;
        const curr = current.data;
        let diff = 0;
        for (let i = 0; i < curr.length; i += 4) {
          diff += Math.abs(curr[i] - prev[i])
            + Math.abs(curr[i + 1] - prev[i + 1])
            + Math.abs(curr[i + 2] - prev[i + 2]);
        }
        const meanDiff = diff / (W * H * 3);

        if (meanDiff < PIXEL_DIFF_THRESHOLD) {
          stabilityCountRef.current = Math.min(stabilityCountRef.current + 1, STABLE_CHECKS_NEEDED);
        } else {
          stabilityCountRef.current = Math.max(0, stabilityCountRef.current - 2);
        }

        const pct = Math.round((stabilityCountRef.current / STABLE_CHECKS_NEEDED) * 100);
        setEstabilidade(pct);

        if (stabilityCountRef.current >= STABLE_CHECKS_NEEDED && !capturedRef.current) {
          capturedRef.current = true;
          capturar();
        }
      }

      lastFrameRef.current = current;
    }, 100);
  }, [capturar]);

  const iniciarCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch (playErr: any) {
          if (playErr?.name === "AbortError") { return; }
          throw playErr;
        }
      }
      iniciarVerificacaoEstabilidade();
    } catch (err: any) {
      if (err?.name === "AbortError") { return; }
      onErro("Não foi possível acessar a câmera.");
    }
  }, [onErro, iniciarVerificacaoEstabilidade]);

  useEffect(() => {
    if (ativa) {
      iniciarCamera();
    } else {
      pararCamera();
    }
    return () => { pararCamera(); };
  }, [ativa, iniciarCamera, pararCamera]);

  const labelEstabilidade = stabilidade >= 100
    ? "Capturando..."
    : stabilidade > 0
    ? "Mantenha a placa estável..."
    : "Posicione a placa no quadro";

  return (
    <div className="text-center">
      <VideoWrapper>
        <StyledVideo ref={videoRef} autoPlay playsInline muted />
        <PlacaFrame $stable={stabilidade}>
          <FrameLabel>Posicione a placa aqui</FrameLabel>
        </PlacaFrame>
      </VideoWrapper>
      <canvas ref={canvasRef} style={{ display: "none" }} />
      <canvas ref={compareRef} style={{ display: "none" }} />
      <StabilityBarWrapper>
        <StabilityBarFill $pct={stabilidade} />
      </StabilityBarWrapper>
      <StabilityLabel>{labelEstabilidade}</StabilityLabel>
      <PrimaryButton outline variante="primary" onClick={() => {
        const canvas = canvasRef.current;
        const video = videoRef.current;
        if (!canvas || !video) { return; }
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext("2d")!.drawImage(video, 0, 0);
        const imagemBase64 = canvas.toDataURL("image/jpeg", 0.9);
        pararCamera();
        (onCapturaManual ?? onCaptura)(imagemBase64);
      }}>
        Capturar Manualmente
      </PrimaryButton>
    </div>
  );
}
